import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/contexts/LanguageContext";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";

interface MemberPaymentsProps {
  memberId: string;
}

export const MemberPayments = ({ memberId }: MemberPaymentsProps) => {
  const { toast } = useToast();
  const { t } = useLanguage();
  const [payments, setPayments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [duesConfig, setDuesConfig] = useState<any[]>([]);

  useEffect(() => {
    loadPayments();
    loadDuesConfig();
  }, [memberId]);

  const loadPayments = async () => {
    try {
      const { data, error } = await supabase
        .from("donations")
        .select("*")
        .eq("member_id", memberId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setPayments(data || []);
    } catch (error: any) {
      toast({
        title: t("member.payments.error"),
        description: t("member.payments.loadError"),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadDuesConfig = async () => {
    try {
      const { data, error } = await supabase
        .from("monthly_dues_config")
        .select("*")
        .order("family_size_min");

      if (error) throw error;
      setDuesConfig(data || []);
    } catch (error: any) {
      console.error("Error loading dues config:", error);
    }
  };

  const handleTithePayment = () => {
    window.open("/donate?type=tithe&member_id=" + memberId, "_blank");
  };

  const handleDuesPayment = () => {
    window.open("/donate?type=monthly_dues&member_id=" + memberId, "_blank");
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{t("member.payments.makePayment")}</CardTitle>
          <CardDescription>{t("member.payments.submitPayment")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">{t("member.payments.tithe")}</CardTitle>
                <CardDescription>
                  {t("member.payments.titheDescription")}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button onClick={handleTithePayment} className="w-full">
                  {t("member.payments.payTithe")}
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">{t("member.payments.monthlyRentalDues")}</CardTitle>
                <CardDescription>
                  {t("member.payments.duesDescription")}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button onClick={handleDuesPayment} className="w-full">
                  {t("member.payments.payMonthlyRentalDues")}
                </Button>
              </CardContent>
            </Card>
          </div>

          <div className="mt-4 p-4 bg-card rounded-lg">
            <h4 className="font-semibold mb-2">{t("member.payments.monthlyPaymentSchedule")}</h4>
            <div className="text-sm">
              {t("member.payments.scheduleAmount")}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("member.payments.history")}</CardTitle>
          <CardDescription>{t("member.payments.historyDescription")}</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p>{t("member.payments.loading")}</p>
          ) : payments.length === 0 ? (
            <p className="text-muted-foreground">{t("member.payments.noHistory")}</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("member.payments.date")}</TableHead>
                  <TableHead>{t("member.payments.type")}</TableHead>
                  <TableHead>{t("member.payments.amount")}</TableHead>
                  <TableHead>{t("member.payments.status")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payments.map((payment) => (
                  <TableRow key={payment.id}>
                    <TableCell>
                      {new Date(payment.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {payment.payment_type === 'monthly_dues' 
                          ? 'Monthly Rental Dues' 
                          : payment.payment_type.replace("_", " ")}
                      </Badge>
                    </TableCell>
                    <TableCell>${payment.amount}</TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          payment.status === "completed" ? "default" : "secondary"
                        }
                      >
                        {payment.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
